/**
 * Created by Alex Xia on 6/16/2020.
 * Description:
 * Add contact panel page
 */
import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { Entity } from '../../tamalelibs/models/entity.model';
import { TabOptions } from '../../widgets/tabs/tabs.model';
import { getContactMsgState, getSlideSheetContactState } from '../../redux/reducers/contact-detail-view.reducer';
import { ContactConfig, ContactAction, ContactActionTypes } from '../../pages/contact/contact.model';
import { ContactDetailConfig } from '../contact-detail/contact-detail.config';
import { Contact } from '../../tamalelibs/models/contact.model';
import { ContactViewModel } from '../../tamalelibs/components/contact.view-model';
import { EDIT_CONTACT_SUCCESS } from '../../tamalelibs/redux/app.actions';
import { lastCustomActionSelector } from '../../redux/reducers/action-history.reducer';
import { RelationshipTreeService } from '../../services/relationship-tree.service';
import { RelationshipTreeConfig, RelationshipTreeActions } from '../relationship-tree/relationship-tree.view-model';
import { AppState } from '../../redux';
import { skip, filter, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { businessConstants } from '../../constants/business.constants';
import { ContactInformationDetail, ContactInformationItemSubject, TriggerSource } from '../contact-detail-new/contact-detail-template/contact-information-item/contact-information-item.model';
import { ContactBasicInformationCategory } from '../contact-detail-new/contact-body-detail.model';
import { ContactDetailService } from '../contact-detail-new/contact-body-detail.service';
import { contactTemplateConfigSelector } from '../../tamalelibs/redux/contact.reducer';
import { ContactComponentCategory } from '../contact-detail-new/contact-body-detail.model';

@Component({
    selector: 'contact-panel',
    templateUrl: './contact-panel.component.html',
    styleUrls: ['./contact-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContactPanelComponent implements OnInit, OnDestroy {
    @Input() entityID: string;
    @Input() eventKey?: string;
    @Input() eventValue?: string;
    @Input() config: ContactConfig;

    currentContact: Contact;
    contactConfig: ContactDetailConfig = new ContactDetailConfig();
    contactActivity = {
        contactID: '',
        isContactActivity: false
    };
    contactPanelActivityConfig = { componentType: ContactComponentCategory.ContactActivity, data: this.contactActivity, position: 3, selectedTab: 0, isCollapsed: '0' };
    relationshipTreeConfig: RelationshipTreeConfig = new RelationshipTreeConfig();
    isPageLoading: boolean;

    // below var is for contact profile data
    contactInformation: Array<ContactInformationDetail> = [];
    isContactProfileHasData = false;
    isContactProfileDataPrepared = false;

    // for tabs
    selectedIndex = 0;
    tabsOptions: TabOptions;
    menuItems: Array<any> = [{
        title: 'Detail',
        disabled: false,
        editable: false,
    }];
    draggableTab = false;

    templateSettings = null;
    triggerSource = TriggerSource.SlideSheet;

    private _subscribed = false;
    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _router: Router,
        private _contactViewModel: ContactViewModel,
        private _changeDetectorRef: ChangeDetectorRef,
        private _relationshipTreeService: RelationshipTreeService,
        private _store: Store<AppState>,
        private _contactBodyDetailService: ContactDetailService,
    ) {
    }

    ngOnInit() {
        this.isPageLoading = true;
        this.contactConfig.embedMode = true;
        this.contactActivity.contactID = this.entityID;

        this._initSubscriptions();
        this._initTwoTabOptions();

        if (this.config && this.config.embedMode) {
            this.config.actionSubject$.subscribe((action: ContactAction) => {
                if (action.type === ContactActionTypes.SWITCH_ANOTHER_CONTACT) {
                    this.selectedIndex = 0;
                    this.isPageLoading = true;
                    this.isContactProfileDataPrepared = false;
                    this._init(action.payload.id);
                }
            });
        } else {
            this._init(this.entityID);
        }
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._subscribed = false;
    }

    selectChange(currentTabIndex: number) {
        this.selectedIndex = currentTabIndex;
        this._updateChangesToUI();
    }

    trackByFn(index, item) {
        return item.id;
    }

    private _initSubscriptions() {
        if (!this._subscribed) {
            this._subscribed = true;

            this._destroySubscriptions.push(
                this._store
                    .pipe<Entity>(select(getSlideSheetContactState))
                    .pipe<Entity>(skip(1))
                    .subscribe(res => this._onSubscribeContact(res)),

                this._store.select(getContactMsgState)
                    .pipe<string>(filter(item => typeof item === 'string'))
                    .subscribe(msg => this._onSubscribeContactMsg(msg)),

                this._store.pipe(
                    select(lastCustomActionSelector),
                ).subscribe(action => this._actionMessageHandler(action)),
            );

            this._relationshipTreeService.relationshipTreeOperate$.subscribe(action => {
                if (action.type === RelationshipTreeActions.REMOVE_RELATIONSHIP
                    || action.type === RelationshipTreeActions.ADD_RELATIONSHIP
                    || action.type === RelationshipTreeActions.CHANGE_RELATIONSHIP) {
                    this._refreshContactPanel();
                }
            });
        }
    }

    private _init(contactId: string) {
        if (contactId) {
            this._contactViewModel.getSlideSheetContact(contactId);
        }
    }

    private _initTwoTabOptions() {
        this.menuItems = [{
            title: 'Detail',
            disabled: false,
            editable: false,
        }, {
            title: 'Relationship',
            disabled: false
        },
        {
            title: 'Activities',
            disabled: false,
            editable: false,
        }];

        this._updateChangesToUI();
    }

    /**
     *  Local refresh
     * @param action
     */
    private _actionMessageHandler(action) {
        if (action.type === EDIT_CONTACT_SUCCESS && this.currentContact && this.currentContact.id) {
            this._init(this.currentContact.id);
        }
    }

    private _initContactInformation() {
        this.contactInformation = [
            {
                subject: ContactInformationItemSubject.ContactSynch,
                value: this.currentContact,
            },
            {
                subject: ContactInformationItemSubject.BasicInformation,
                value: this._contactBodyDetailService.getContactBasicInformation(this.currentContact, this.templateSettings),
            },
            {
                subject: ContactInformationItemSubject.ContactInformation,
                value: this._contactBodyDetailService.getContactConnectDetail(this.currentContact),
            },
            {
                subject: ContactInformationItemSubject.CurrentEmployment,
                value: this._contactBodyDetailService.getContactEmploymentDetail(this.currentContact).currentEmploymentInfo,
            },
            {
                subject: ContactInformationItemSubject.FormerEmployment,
                value: this._contactBodyDetailService.getContactEmploymentDetail(this.currentContact).formerEmploymentInfo,
            },
            {
                subject: ContactInformationItemSubject.Address,
                value: this._contactBodyDetailService.getContactAddress(this.currentContact),
            },
            {
                subject: ContactInformationItemSubject.Internet,
                value: this._contactBodyDetailService.getContactSocialMedia(this.currentContact),
            },
            {
                subject: ContactInformationItemSubject.Other,
                value: this._contactBodyDetailService.getContactOtherInformation(this.currentContact, this.templateSettings),
            },
            {
                subject: ContactInformationItemSubject.Note,
                value: [
                    {
                        title: ContactInformationItemSubject.Note,
                        value: [{
                            clickable: false,
                            value: this.currentContact.notes,
                        }]
                    }
                ]
            },
        ];
        this.contactInformation.forEach(element => {
            if (element.subject === ContactBasicInformationCategory.Note) {
                if (element.value[0].value[0].value) {
                    this.isContactProfileHasData = true;
                }
            } else if (element.value instanceof Array && element.value.length !== 0) {
                this.isContactProfileHasData = true;
            }
        });
        if (!this.isContactProfileHasData) {
            this.contactInformation = [];
        }
    }

    private _onSubscribeContact(_contact: Contact): void {
        if (_contact) {
            this.contactConfig.contact = this.currentContact = _contact;
            this.contactConfig.actions$.next('');
            this._showRelationshipTree(_contact);
            this._destroySubscriptions.push(
                this._store.pipe(
                    select(contactTemplateConfigSelector),
                    map(res => res.templateSetting),
                ).subscribe(res => {
                    this.templateSettings = res;
                    this._initContactInformation();
                    this.isPageLoading = false;
                    this.isContactProfileDataPrepared = true;
                    this._updateChangesToUI();
                })
            );
        }
    }

    private _showRelationshipTree(_entity: Entity) {
        this.relationshipTreeConfig.checkable = false;
        this.relationshipTreeConfig.expandable = true;

        const treeNodes = RelationshipTreeService.parseToTreeViewModel(_entity);
        this.relationshipTreeConfig.nodes = treeNodes;
        this.relationshipTreeConfig.showAs = 'shortName';
        this.relationshipTreeConfig.expandedKeys = [];
        treeNodes.forEach(element => {
            if (element.uuid !== '1' && element.level !== '4') {
                this.relationshipTreeConfig.expandedKeys.push(element.uuid);
            }
        });

        this._updateChangesToUI();
    }

    private _onSubscribeContactMsg(msg: string) {
        if (msg.indexOf('GET_CONTACT_FAILURE') > -1) {
            this._router.navigate(['/error']);
        }
    }

    private _refreshContactPanel() {
        if (this.currentContact.id) {
            this._init(this.currentContact.id);
        }
    }

    private _updateChangesToUI() {
        if (!this._changeDetectorRef[businessConstants.common.changeDetectorDestroyedIdentifier]) {
            this._changeDetectorRef.detectChanges();
        }
    }

}
